/**
 * ChartBlock — text2sql 조회 결과를 차트로 렌더링
 *
 * 자동 피벗: 3컬럼(X축, 그룹, 수치) 구조 감지 시 자동 피벗 후 다계열 차트 생성
 * - 날짜/월 컬럼 포함: 선형차트
 * - 카테고리 5개 이하 + 수치 1개: 파이차트
 * - 그 외: 막대차트 fallback
 */

import { useState } from 'react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

type ChartType = 'bar' | 'line' | 'pie';

interface ChartBlockProps {
  rows: Record<string, unknown>[];
}

const COLORS = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316', '#14b8a6'];

// 숫자형 컬럼 감지
function isNumericCol(rows: Record<string, unknown>[], col: string): boolean {
  return rows.slice(0, 5).some(r => {
    const v = r[col];
    return v !== null && v !== undefined && !isNaN(Number(v));
  });
}

// 날짜/월 컬럼 감지
function isDateCol(col: string): boolean {
  return /월|날|일시|일자|date|month|year|time/i.test(col);
}

// ── 자동 피벗 ───────────────────────────────────────────────────────────────
/**
 * 3컬럼(xCol, groupCol, valueCol) 구조를 감지해 피벗 데이터로 변환.
 * 예: [{월:'2026-01', 담당자:'강다슬', 매출:500}, ...] →
 *     [{월:'2026-01', 강다슬:500, 김철수:0}, ...]
 */
function tryPivot(
  rows: Record<string, unknown>[],
  keys: string[],
  numericKeys: string[]
): { pivoted: Record<string, unknown>[]; xCol: string; seriesKeys: string[] } | null {
  // 조건: 컬럼 3개, 수치 1개, 비수치 2개
  if (keys.length !== 3 || numericKeys.length !== 1) return null;

  const valueCol = numericKeys[0]!;
  const labelCols = keys.filter(k => k !== valueCol);
  if (labelCols.length !== 2) return null;

  // X축 후보: 날짜 컬럼 우선, 없으면 첫 번째
  const xCol = labelCols.find(isDateCol) ?? labelCols[0]!;
  const groupCol = labelCols.find(c => c !== xCol)!;

  // 그룹값 추출
  const groups = [...new Set(rows.map(r => String(r[groupCol] ?? '')))].filter(Boolean);
  if (groups.length < 2 || groups.length > 20) return null; // 너무 많거나 1개면 피벗 불필요

  // 피벗
  const xValues = [...new Set(rows.map(r => String(r[xCol] ?? '')))];
  const pivoted = xValues.map(x => {
    const entry: Record<string, unknown> = { [xCol]: x };
    for (const g of groups) {
      const match = rows.find(r => String(r[xCol]) === x && String(r[groupCol]) === g);
      entry[g] = match ? Number(match[valueCol]) || 0 : 0;
    }
    return entry;
  });

  return { pivoted, xCol, seriesKeys: groups };
}

// ── 차트 타입 자동 결정 ──────────────────────────────────────────────────────
function detectChartType(
  keys: string[],
  numericKeys: string[],
  rowCount: number,
  xCol: string
): ChartType {
  if (isDateCol(xCol)) return 'line';
  if (numericKeys.length === 1 && rowCount <= 8 && keys.length === 2) return 'pie';
  return 'bar';
}

export function ChartBlock({ rows }: ChartBlockProps) {
  const [chartType, setChartType] = useState<ChartType | null>(null);
  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set());

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleLegendClick = (e: any) => {
    const key = String(e.dataKey ?? '');
    if (!key) return;
    setHiddenSeries(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (!rows || rows.length === 0) return null;

  const keys = Object.keys(rows[0]);
  const numericKeys = keys.filter(k => isNumericCol(rows, k));
  if (numericKeys.length === 0) return null;

  // 피벗 시도
  const pivotResult = tryPivot(rows, keys, numericKeys);

  let data: Record<string, unknown>[];
  let xCol: string;
  let seriesKeys: string[];

  if (pivotResult) {
    // 피벗 성공 → 다계열 차트
    data = pivotResult.pivoted;
    xCol = pivotResult.xCol;
    seriesKeys = pivotResult.seriesKeys;
  } else {
    // 피벗 불필요 → 기존 방식
    xCol = keys.find(k => !numericKeys.includes(k)) || keys[0]!;
    seriesKeys = numericKeys;
    data = rows.map(r => {
      const entry: Record<string, unknown> = { [xCol]: r[xCol] };
      for (const k of numericKeys) entry[k] = Number(r[k]) || 0;
      return entry;
    });
  }

  const autoType = detectChartType(keys, numericKeys, rows.length, xCol);
  const activeType = chartType ?? autoType;

  const renderChart = () => {
    if (activeType === 'line') {
      return (
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={data} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey={xCol} tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            <Legend onClick={handleLegendClick} style={{ cursor: 'pointer' }} />
            {seriesKeys.map((k, i) => (
              <Line
                key={k} type="monotone" dataKey={k}
                stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={{ r: 3 }}
                hide={hiddenSeries.has(k)}
                strokeOpacity={hiddenSeries.has(k) ? 0.2 : 1}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      );
    }

    if (activeType === 'pie' && seriesKeys.length === 1) {
      const numKey = seriesKeys[0]!;
      return (
        <ResponsiveContainer width="100%" height={280}>
          <PieChart>
            <Pie data={data} dataKey={numKey} nameKey={xCol} cx="50%" cy="50%" outerRadius={110} label>
              {data.map((entry, i) => (
                <Cell
                  key={i}
                  fill={COLORS[i % COLORS.length]}
                  opacity={hiddenSeries.has(String(entry[xCol])) ? 0.2 : 1}
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleLegendClick({ dataKey: String(entry[xCol]) })}
                />
              ))}
            </Pie>
            <Tooltip />
            <Legend onClick={handleLegendClick} style={{ cursor: 'pointer' }} />
          </PieChart>
        </ResponsiveContainer>
      );
    }

    // 막대차트 (기본)
    return (
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={data} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis dataKey={xCol} tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip />
          <Legend onClick={handleLegendClick} style={{ cursor: 'pointer' }} />
          {seriesKeys.map((k, i) => (
            <Bar
              key={k} dataKey={k}
              fill={COLORS[i % COLORS.length]} radius={[3, 3, 0, 0]}
              hide={hiddenSeries.has(k)}
              fillOpacity={hiddenSeries.has(k) ? 0.2 : 1}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    );
  };

  return (
    <div className="chat-chart-block">
      <div className="chat-chart-tabs">
        {(['bar', 'line', 'pie'] as ChartType[]).map(t => (
          <button
            key={t}
            className={`chat-chart-tab${activeType === t ? ' active' : ''}`}
            onClick={() => setChartType(t)}
          >
            {t === 'bar' ? '막대' : t === 'line' ? '선형' : '파이'}
          </button>
        ))}
        <span className="chat-chart-count">
          {rows.length}행{pivotResult ? ` → ${pivotResult.seriesKeys.length}개 계열` : ''}
        </span>
      </div>
      {renderChart()}
    </div>
  );
}
